import React, { useCallback, useEffect, useMemo, useState } from 'react'
import {
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControl,
    IconButton,
    List,
    ListItem,
    ListItemText,
    MenuItem,
    Select,
    Typography,
    makeStyles,
    Theme,
} from '@material-ui/core'
import AddIcon from '@material-ui/icons/Add'
import ArrowDownwardIcon from '@material-ui/icons/ArrowDownward'
import ArrowUpwardIcon from '@material-ui/icons/ArrowUpward'
import RemoveCircleOutlineIcon from '@material-ui/icons/RemoveCircleOutline'

import { PipelineConfigSource } from './usePipelineConfigApi'
import {
    EditStepsApplyPayload,
    PipelineDefinition,
    PipelineStepInstance,
    PipelineStepType,
} from './pipelineBuilder.types'
import { getEnabledStepsInOrder } from './stepOrder'
import { PIPELINE_STEP_ORDER, STEP_CATALOG } from './stepCatalog'
import { createStepInstanceForDialog } from './pipelineBuilderStateUtils'
import {
    isRequiredStepType,
    isStepRemovable,
    isStepRepeatable,
} from './stepGuards'
import { getStepDisplayLabel } from './stepDisplayUtils'

const useStyles = makeStyles((theme: Theme) => ({
    section: {
        marginBottom: theme.spacing(2),
    },
    sectionTitle: {
        fontWeight: 600,
        fontSize: '0.8125rem',
        marginBottom: theme.spacing(1),
        color: theme.palette.text.secondary,
        textTransform: 'uppercase',
        letterSpacing: '0.04em',
    },
    listItem: {
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: theme.spacing(0.75),
        marginBottom: theme.spacing(0.75),
        backgroundColor: theme.palette.background.paper,
        padding: theme.spacing(1.25, 1.5),
        display: 'block',
    },
    listItemInPipeline: {
        borderColor: theme.palette.primary.main + '55',
        backgroundColor: theme.palette.primary.main + '08',
    },
    listItemInner: {
        display: 'flex',
        alignItems: 'flex-start',
        gap: theme.spacing(2),
    },
    listItemText: {
        flex: 1,
        minWidth: 0,
    },
    listItemActions: {
        display: 'flex',
        alignItems: 'center',
        flexShrink: 0,
        gap: theme.spacing(0.25),
        paddingTop: theme.spacing(0.25),
    },
    loadRow: {
        display: 'flex',
        gap: theme.spacing(1),
        alignItems: 'center',
    },
    loadSelect: {
        flex: 1,
        minWidth: 240,
    },
    selectPlaceholder: {
        color: theme.palette.text.secondary,
    },
    emptyHint: {
        color: theme.palette.text.secondary,
        fontSize: '0.8125rem',
        fontStyle: 'italic',
    },
    loadHint: {
        color: theme.palette.text.secondary,
        fontSize: '0.8125rem',
        marginTop: theme.spacing(0.75),
    },
}))

interface EditStepsDialogProps {
    open: boolean
    enabledSteps: PipelineStepInstance[]
    orderedSteps: PipelineStepInstance[]
    readOnly?: boolean
    previousConfigOptions: PipelineConfigSource[]
    apartmentId?: number
    onFetchConfigDefinition: (
        apartmentId: number,
        configId: number,
    ) => Promise<PipelineDefinition>
    onClose: () => void
    onApply: (payload: EditStepsApplyPayload) => void
}

export const EditStepsDialog: React.FC<EditStepsDialogProps> = ({
    open,
    enabledSteps,
    orderedSteps,
    readOnly = false,
    previousConfigOptions,
    apartmentId,
    onFetchConfigDefinition,
    onClose,
    onApply,
}) => {
    const classes = useStyles()
    const [allSteps, setAllSteps] = useState<PipelineStepInstance[]>([])
    const [pipelineSteps, setPipelineSteps] = useState<PipelineStepInstance[]>(
        [],
    )
    const [removedStepIds, setRemovedStepIds] = useState<string[]>([])
    const [sourceConfigId, setSourceConfigId] = useState<number | ''>('')
    const [isLoadingSource, setIsLoadingSource] = useState(false)

    const syncFromProps = useCallback(() => {
        setAllSteps(orderedSteps.map((s) => ({ ...s })))
        setPipelineSteps(enabledSteps.map((s) => ({ ...s })))
        setRemovedStepIds([])
        setSourceConfigId('')
    }, [enabledSteps, orderedSteps])

    useEffect(() => {
        if (open) {
            syncFromProps()
        }
    }, [open, syncFromProps])

    const pipelineCountByType = useMemo(() => {
        const counts = new Map<PipelineStepType, number>()
        for (const step of pipelineSteps) {
            counts.set(step.type, (counts.get(step.type) ?? 0) + 1)
        }
        return counts
    }, [pipelineSteps])

    const moveStep = (index: number, direction: -1 | 1) => {
        const step = pipelineSteps[index]
        if (isRequiredStepType(step.type)) {
            return
        }
        const target = index + direction
        if (target < 2 || target >= pipelineSteps.length) {
            return
        }
        setPipelineSteps((prev) => {
            const next = [...prev]
            const [moved] = next.splice(index, 1)
            next.splice(target, 0, moved)
            return next
        })
    }

    const removeFromPipeline = (stepId: string) => {
        setPipelineSteps((prev) => prev.filter((s) => s.id !== stepId))
        setAllSteps((prev) =>
            prev.map((s) => (s.id === stepId ? { ...s, enabled: false } : s)),
        )
    }

    const addToPipeline = (type: PipelineStepType) => {
        if (!isStepRepeatable(type)) {
            const existing = allSteps.find(
                (s) => s.type === type && !removedStepIds.includes(s.id),
            )

            if (existing) {
                const updated = { ...existing, enabled: true }
                setAllSteps((prev) =>
                    prev.map((s) => (s.id === existing.id ? updated : s)),
                )
                setPipelineSteps((prev) => [...prev, updated])
                return
            }
        }

        const newStep = createStepInstanceForDialog(type, [
            ...allSteps,
            ...pipelineSteps,
        ])
        setAllSteps((prev) => [...prev, newStep])
        setPipelineSteps((prev) => [...prev, newStep])
    }

    const handleLoadFromConfig = async () => {
        if (!sourceConfigId) {
            return
        }
        const source = previousConfigOptions.find(
            (s) => s.id === sourceConfigId,
        )
        const sourceApartmentId = source?.apartment_id ?? apartmentId
        if (sourceApartmentId == null) {
            return
        }
        setIsLoadingSource(true)
        try {
            const definition = await onFetchConfigDefinition(
                sourceApartmentId,
                sourceConfigId,
            )
            const loadedSteps = definition.steps.map((s) => ({ ...s }))
            setAllSteps(loadedSteps)
            setPipelineSteps(
                getEnabledStepsInOrder(loadedSteps).map((s) => ({ ...s })),
            )
            setRemovedStepIds([])
        } finally {
            setIsLoadingSource(false)
        }
    }

    const handleApply = () => {
        onApply({
            benchSteps: pipelineSteps.map((s) => ({ ...s, enabled: true })),
            removedStepIds,
            allStepsOverride: allSteps.map((s) => ({ ...s })),
        })
        onClose()
    }

    const renderConfigValue = (selected: unknown) => {
        if (!selected) {
            return (
                <span className={classes.selectPlaceholder}>
                    Select a configuration…
                </span>
            )
        }
        const config = previousConfigOptions.find((s) => s.id === selected)
        return config?.name ?? ''
    }

    return (
        <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
            <DialogTitle>Edit pipeline steps</DialogTitle>
            <DialogContent dividers>
                {!readOnly && apartmentId != null && (
                    <Box className={classes.section}>
                        <Typography className={classes.sectionTitle}>
                            Copy from another configuration
                        </Typography>
                        <Box className={classes.loadRow}>
                            <FormControl
                                variant="outlined"
                                size="small"
                                className={classes.loadSelect}
                                disabled={previousConfigOptions.length === 0}
                            >
                                <Select
                                    displayEmpty
                                    value={sourceConfigId}
                                    onChange={(e) =>
                                        setSourceConfigId(
                                            e.target.value as number,
                                        )
                                    }
                                    renderValue={renderConfigValue}
                                >
                                    {previousConfigOptions.map((config) => (
                                        <MenuItem
                                            key={config.id}
                                            value={config.id}
                                        >
                                            {config.name}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                            <Button
                                variant="outlined"
                                size="small"
                                disabled={
                                    !sourceConfigId ||
                                    isLoadingSource ||
                                    previousConfigOptions.length === 0
                                }
                                onClick={handleLoadFromConfig}
                            >
                                Load
                            </Button>
                        </Box>
                        {previousConfigOptions.length === 0 ? (
                            <Typography className={classes.loadHint}>
                                No other pipeline configurations are available
                                to copy from yet.
                            </Typography>
                        ) : (
                            <Typography className={classes.loadHint}>
                                Loads that configuration&apos;s step layout into
                                this dialog. Click Done to save for this
                                configuration.
                            </Typography>
                        )}
                    </Box>
                )}

                <Box className={classes.section}>
                    <Typography className={classes.sectionTitle}>
                        In pipeline
                    </Typography>
                    {pipelineSteps.length === 0 ? (
                        <Typography className={classes.emptyHint}>
                            No steps in the pipeline yet. Add steps from the
                            list below or copy from another configuration.
                        </Typography>
                    ) : (
                        <List disablePadding>
                            {pipelineSteps.map((step, index) => {
                                const catalog = STEP_CATALOG[step.type]
                                return (
                                    <ListItem
                                        key={step.id}
                                        className={`${classes.listItem} ${classes.listItemInPipeline}`}
                                        dense
                                        disableGutters
                                    >
                                        <Box className={classes.listItemInner}>
                                            <Box
                                                className={classes.listItemText}
                                            >
                                                <ListItemText
                                                    primary={`${
                                                        index + 1
                                                    }. ${getStepDisplayLabel(
                                                        step,
                                                    )}`}
                                                    secondary={
                                                        catalog.description
                                                    }
                                                />
                                            </Box>
                                            {!readOnly &&
                                                isStepRemovable(step.type) && (
                                                    <Box
                                                        className={
                                                            classes.listItemActions
                                                        }
                                                    >
                                                        <IconButton
                                                            size="small"
                                                            aria-label="Move up"
                                                            disabled={
                                                                index <= 1
                                                            }
                                                            onClick={() =>
                                                                moveStep(
                                                                    index,
                                                                    -1,
                                                                )
                                                            }
                                                        >
                                                            <ArrowUpwardIcon fontSize="small" />
                                                        </IconButton>
                                                        <IconButton
                                                            size="small"
                                                            aria-label="Move down"
                                                            disabled={
                                                                index >=
                                                                pipelineSteps.length -
                                                                    1
                                                            }
                                                            onClick={() =>
                                                                moveStep(
                                                                    index,
                                                                    1,
                                                                )
                                                            }
                                                        >
                                                            <ArrowDownwardIcon fontSize="small" />
                                                        </IconButton>
                                                        <IconButton
                                                            size="small"
                                                            aria-label="Remove from pipeline"
                                                            onClick={() =>
                                                                removeFromPipeline(
                                                                    step.id,
                                                                )
                                                            }
                                                        >
                                                            <RemoveCircleOutlineIcon fontSize="small" />
                                                        </IconButton>
                                                    </Box>
                                                )}
                                        </Box>
                                    </ListItem>
                                )
                            })}
                        </List>
                    )}
                </Box>

                <Box className={classes.section}>
                    <Typography className={classes.sectionTitle}>
                        All steps
                    </Typography>
                    <List disablePadding>
                        {PIPELINE_STEP_ORDER.map((type) => {
                            const catalog = STEP_CATALOG[type]
                            const countInPipeline =
                                pipelineCountByType.get(type) ?? 0
                            const inPipeline = countInPipeline > 0
                            const repeatable = isStepRepeatable(type)
                            return (
                                <ListItem
                                    key={type}
                                    className={classes.listItem}
                                    dense
                                    disableGutters
                                >
                                    <Box className={classes.listItemInner}>
                                        <Box className={classes.listItemText}>
                                            <ListItemText
                                                primary={catalog.label}
                                                secondary={catalog.description}
                                            />
                                        </Box>
                                        <Box
                                            className={classes.listItemActions}
                                        >
                                            {inPipeline && !repeatable ? (
                                                <Typography
                                                    variant="caption"
                                                    color="primary"
                                                >
                                                    In pipeline
                                                </Typography>
                                            ) : (
                                                !readOnly && (
                                                    <>
                                                        {repeatable &&
                                                            countInPipeline >
                                                                0 && (
                                                                <Typography
                                                                    variant="caption"
                                                                    color="textSecondary"
                                                                    style={{
                                                                        marginRight: 4,
                                                                    }}
                                                                >
                                                                    {
                                                                        countInPipeline
                                                                    }{' '}
                                                                    in pipeline
                                                                </Typography>
                                                            )}
                                                        <IconButton
                                                            size="small"
                                                            color="primary"
                                                            aria-label={`Add ${catalog.label} to pipeline`}
                                                            onClick={() =>
                                                                addToPipeline(
                                                                    type,
                                                                )
                                                            }
                                                        >
                                                            <AddIcon fontSize="small" />
                                                        </IconButton>
                                                    </>
                                                )
                                            )}
                                        </Box>
                                    </Box>
                                </ListItem>
                            )
                        })}
                    </List>
                </Box>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Cancel</Button>
                <Button
                    onClick={handleApply}
                    color="primary"
                    variant="contained"
                    disabled={readOnly}
                >
                    Done
                </Button>
            </DialogActions>
        </Dialog>
    )
}
